import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * PortalDropdown — Renders a dropdown menu via React Portal at document.body
 * to escape any parent overflow:hidden / clipping context.
 *
 * Position is computed from the trigger's bounding rect (right-aligned by default).
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   const triggerRef = useRef(null);
 *   <button ref={triggerRef} onClick={() => setOpen(!open)}>...</button>
 *   <PortalDropdown open={open} onClose={() => setOpen(false)} triggerRef={triggerRef}>
 *     <div>menu content</div>
 *   </PortalDropdown>
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - triggerRef: ref to the trigger button (for positioning)
 *   - align: "left" | "right" | "center"  (default "right")
 *   - width: number (default auto)
 *   - className: extra classes for the menu container
 *   - children: menu content
 */
export function PortalDropdown({ open, onClose, triggerRef, align = "right", width, className = "", children }) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const trigger = triggerRef?.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const w = width || Math.max(rect.width, 180);
      let left = rect.left;
      if (align === "right") left = rect.right - w;
      else if (align === "center") left = rect.left + (rect.width - w) / 2;
      // Clamp to viewport
      left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
      // Vertical : flip up if not enough room below
      const menuHeight = menuRef.current?.offsetHeight || 240;
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const top = spaceBelow < menuHeight && rect.top > menuHeight + 4
        ? rect.top - menuHeight - 4
        : rect.bottom + 4;
      setPos({ top, left, width: w });
    };
    update();
    // Re-measure once menu height is known after first render
    const t = setTimeout(update, 0);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, triggerRef, align, width]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (triggerRef?.current && triggerRef.current.contains(e.target)) return;
      // Ignore clicks inside any Radix UI portal (Select, Dropdown, Popover, etc.)
      // — those render at document.body level too and would otherwise wrongly
      // close us on item selection.
      if (e.target?.closest && e.target.closest("[data-radix-popper-content-wrapper], [data-radix-portal]")) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, triggerRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={menuRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
      className={"rounded-lg border border-white/10 bg-[#14101c] shadow-2xl overflow-hidden " + className}
    >
      {children}
    </div>,
    document.body,
  );
}

export default PortalDropdown;
